import { useEffect, useRef, useState } from 'react';

import type {
    FormattedAccountReportType,
    FormattedCompetitorReportType,
    FormattedDashboardReportType,
    FormattedHashtagReportType
} from '@/utils/hooks/reports/useEditReport';
import type { HashtagType } from '@/utils/hooks/swr/useHashtags';
import useTranslation from '@/utils/hooks/translation/useTranslation';

// Utils
import useIntersectionObserver from '@/utils/hooks/utils/useIntersectionObserver';

import type { DashboardType } from '@/types/dashboardType';
import type { SocialProfileType } from '@/types/socialProfileType';

// Lib
import { REPORT_TYPES } from '@/lib/reports/reportFormTypesConfig';

// Components
import InfoBanner from '@/components/base/InfoBanner';
import ReportFormBody from '@/components/pages/reports/CreateEditPage/components/ReportForm/ReportFormBody';
import ReportFormBodyLoading from '@/components/pages/reports/CreateEditPage/components/ReportForm/ReportFormBodyLoading';
// Types
import type { ReportFormErrorFallbackProps } from '@/components/pages/reports/CreateEditPage/components/ReportForm/ReportFormErrorFallback';
import ReportFormErrorFallback from '@/components/pages/reports/CreateEditPage/components/ReportForm/ReportFormErrorFallback';
import ReportFormHeader from '@/components/pages/reports/CreateEditPage/components/ReportForm/ReportFormHeader';

export type FrequencyType =
    | 'every/day'
    | 'every/week'
    | 'every/month'
    | 'every/quarter'
    | 'every/year';

type ReportBaseFormProps = {
    isEdit?: boolean;
    isLoading: boolean;
    hasError: boolean;
    mutateContent: ReportFormErrorFallbackProps['relaunchRequest'];
};

type ReportAccountFormProps = ReportBaseFormProps & {
    type: typeof REPORT_TYPES.ACCOUNT;
    contentList?: SocialProfileType[];
    initialData?: FormattedAccountReportType;
};

type ReportDashboardFormProps = ReportBaseFormProps & {
    type: typeof REPORT_TYPES.DASHBOARD;
    contentList?: DashboardType[];
    initialData?: FormattedDashboardReportType;
};

type ReportHashtagFormProps = ReportBaseFormProps & {
    type: typeof REPORT_TYPES.HASHTAG;
    contentList?: HashtagType[];
    initialData?: FormattedHashtagReportType;
};

type ReportCompetitorFormProps = ReportBaseFormProps & {
    type: typeof REPORT_TYPES.COMPETITOR;
    contentList?: SocialProfileType[];
    initialData?: FormattedCompetitorReportType;
};

export type ReportFormProps =
    | ReportAccountFormProps
    | ReportDashboardFormProps
    | ReportHashtagFormProps
    | ReportCompetitorFormProps;

const ReportForm = ({
    isEdit = false,
    isLoading,
    hasError,
    mutateContent,
    ...restProps
}: ReportFormProps) => {
    const { t } = useTranslation();
    const [globalError, setGlobalError] = useState(false);
    const refBannerInfo = useRef<HTMLDivElement | null>(null);
    const bannerInfoInViewPort = useIntersectionObserver(refBannerInfo, { threshold: 0 });

    useEffect(() => {
        // Scroll To info banner if it's not visible in the viewport
        if (refBannerInfo?.current && !bannerInfoInViewPort && globalError) {
            refBannerInfo?.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [globalError, bannerInfoInViewPort]);

    return (
        <>
            <div ref={refBannerInfo} />
            {globalError ? (
                <InfoBanner
                    type="error"
                    title={t('errors:error_occured')}
                    message={t('errors:error_occured_submit_data')}
                    className="mb-5 w-full"
                    onClose={() => setGlobalError(false)}
                />
            ) : null}
            <div className="flex w-full flex-1 flex-col rounded-md bg-white px-5 py-10 dark:bg-icoDarkMode-anthracite sm:px-8 lg:px-12 xl:px-16">
                {!(isEdit && hasError) ? (
                    <section className="mb-10 flex w-full flex-col">
                        <ReportFormHeader
                            currentType={restProps.type}
                            isEdit={isEdit}
                            isLoading={isEdit ? isLoading : false}
                        />
                    </section>
                ) : null}
                {hasError ? (
                    <ReportFormErrorFallback relaunchRequest={mutateContent} />
                ) : (
                    <section className="flex w-full flex-col">
                        {isLoading ? (
                            <ReportFormBodyLoading isEdit={isEdit} />
                        ) : (
                            <ReportFormBody setGlobalError={setGlobalError} {...restProps} />
                        )}
                    </section>
                )}
            </div>
        </>
    );
};

export default ReportForm;
